import { useEffect, useMemo, useRef, useState } from 'react'
import { Info } from 'lucide-react'
import { useAppStore } from '@core/store'
import { useAuth } from '@core/auth/useAuth'
import { rawIfc, type RawIfcStatus } from '@viewer/lib/rawIfc'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  ifcPersistence,
  type IfcBlobRecord,
} from '@core/session/ifcPersistence'
import {
  estimateOriginStorage,
  formatBytes,
} from '@viewer/lib/storageInfo'
import { useShortcutHandler } from '@core/shortcuts'
import {
  CHANNEL,
  FEATURES,
  HELP,
  LIMITATIONS,
  RELEASES,
  VERSION,
  type FeatureGroup,
  type KnownLimitation,
  type ReleaseNote,
} from '@core/release'

// App-level popover with five tabs:
//   Versão        - release notes + build provenance
//   Funcionalidades - what the platform can do (filtered by enabled modules)
//   Limitações    - known limitations / caveats
//   Diagnóstico   - runtime diagnostics (parser, fragments, cache)
//   Ajuda         - quickstart + how to report bugs
//   Créditos      - third-party work the app builds on (licences + links)
type Tab =
  | 'versao'
  | 'funcionalidades'
  | 'limitacoes'
  | 'diagnostico'
  | 'ajuda'
  | 'creditos'

const TABS: { id: Tab; label: string }[] = [
  { id: 'versao', label: 'Versão' },
  { id: 'funcionalidades', label: 'Funcionalidades' },
  { id: 'limitacoes', label: 'Limitações' },
  { id: 'diagnostico', label: 'Diagnóstico' },
  { id: 'ajuda', label: 'Ajuda' },
  { id: 'creditos', label: 'Créditos' },
]

// Créditos: trabalho de terceiros que o viewer usa. Licenças verificadas
// nos package.json / ficheiros LICENSE de cada pacote em node_modules
// (não citadas de memória). Regra da casa: creditar sempre que usamos
// algo de outros.
type Credit = {
  name: string
  use: string
  licence: string
  href: string
}

const CREDITS: Credit[] = [
  {
    name: 'ThatOpen Engine (@thatopen/components, @thatopen/fragments)',
    use: 'Motor de visualização IFC e geometria em fragmentos.',
    licence: 'MIT',
    href: 'https://github.com/ThatOpen/engine_components',
  },
  {
    name: 'web-ifc',
    use: 'Parser de IFC em WebAssembly (leitura do modelo). As tabelas do esquema IFC2X3, IFC4 e IFC4X3 que traz (atributos, inversos e herança, geradas dos EXPRESS da buildingSMART) dão os nomes no painel Estrutura IFC. Desde a beta.201 é uma compilação nossa do ramo principal (commit dd44aa7), sem alterações, até sair a versão 0.0.79.',
    licence: 'MPL-2.0',
    href: 'https://github.com/ThatOpen/engine_web-ifc',
  },
  {
    name: 'three.js',
    use: 'Renderização 3D (WebGL) no viewport, e o leitor de PLY das ' +
      'nuvens de pontos (three/examples/jsm/loaders/PLYLoader).',
    licence: 'MIT',
    href: 'https://threejs.org',
  },
  {
    name: 'React',
    use: 'Base da interface da aplicação.',
    licence: 'MIT',
    href: 'https://react.dev',
  },
  {
    name: 'React Router',
    use: 'Navegação entre vistas e workspaces.',
    licence: 'MIT',
    href: 'https://reactrouter.com',
  },
  {
    name: 'Zustand',
    use: 'Gestão de estado da aplicação.',
    licence: 'MIT',
    href: 'https://github.com/pmndrs/zustand',
  },
  {
    name: 'Leaflet',
    use: 'Mapa 2D no painel Mapa.',
    licence: 'BSD-2-Clause',
    href: 'https://leafletjs.com',
  },
  {
    name: 'proj4js',
    use: 'Conversão de coordenadas entre sistemas de referência (CRS/EPSG).',
    licence: 'MIT',
    href: 'https://github.com/proj4js/proj4js',
  },
  {
    name: 'fflate',
    use: 'Compressão zip (exportação KMZ e ficheiros de sessão).',
    licence: 'MIT',
    href: 'https://github.com/101arrowz/fflate',
  },
  {
    name: 'Lucide',
    use: 'Ícones da interface (via lucide-react).',
    licence: 'ISC',
    href: 'https://lucide.dev',
  },
  {
    name: 'Tailwind CSS',
    use: 'Estilos e layout da interface.',
    licence: 'MIT',
    href: 'https://tailwindcss.com',
  },
  {
    name: 'OpenStreetMap',
    use: 'Tiles do mapa 2D, do chão do basemap 3D e do globo 3D (© OpenStreetMap contributors).',
    licence: 'ODbL 1.0',
    href: 'https://www.openstreetmap.org/copyright',
  },
  {
    name: 'Mapzen / AWS Terrain Tiles',
    use: 'Tiles de relevo (DEM terrarium) para o chão 3D com terreno e para o relevo do globo 3D.',
    licence: 'AWS Open Data (fontes várias)',
    href: 'https://registry.opendata.aws/terrain-tiles/',
  },
  {
    name: 'CesiumJS',
    use: 'Motor do globo 3D. Os ficheiros vêm da nossa origem; não se usa o Cesium ion (nem contas nem chaves).',
    licence: 'Apache-2.0',
    href: 'https://github.com/CesiumGS/cesium',
  },
  {
    name: 'EGM96 (NGA), na distribuição do GeographicLib',
    use: 'Ondulação do geoide para pôr alturas ortométricas no globo 3D (grelha de 0,5 grau incluída).',
    licence: 'Domínio público (EGM96); MIT (GeographicLib)',
    href: 'https://geographiclib.sourceforge.io/C++/doc/geoid.html',
  },
  {
    name: 'Grelhas de mudança de datum (PROJ-data)',
    use: 'Precisão de 1 a 10 cm na passagem para WGS 84 de Datum 73 e Datum Lisboa (Direção-Geral do Território), ED50 (IGN Espanha), OSGB36 (Ordnance Survey), RD (NSGI), BD72 (IGN Bélgica), DHDN (AdV) e CH1903 (swisstopo). Incluídas e servidas pela nossa origem.',
    licence: 'CC-BY-4.0 (DGT, IGN, NSGI, IGN-BE), BSD-2-Clause (OS), CC0 (swisstopo), redistribuição livre (AdV)',
    href: 'https://github.com/OSGeo/PROJ-data',
  },
  {
    name: 'geotiff.js',
    use: 'Leitura das grelhas de mudança de datum (GeoTIFF).',
    licence: 'MIT',
    href: 'https://github.com/geotiffjs/geotiff.js',
  },
  {
    name: 'PROJ',
    use: 'Base de dados de onde se gerou o índice EPSG incluído (definições, áreas de uso e transformações para WGS 84), via pyproj.',
    licence: 'MIT',
    href: 'https://proj.org',
  },
  {
    name: 'Massing PDF (@massingcloud/pdf-viewer)',
    use: 'Motor da revisão de desenhos PDF: anotações, medições ' +
      'calibradas, pins, comparação de revisões e intercâmbio ' +
      'BCF/XFDF. Redistribuído já construído dentro deste repositório ' +
      '(frontend/vendor/massing-pdf), com a licença incluída. A ' +
      'interface do motor está em inglês: não expõe tradução.',
    licence: 'MIT (Matthew M. Emma)',
    href: 'https://github.com/MassingCloud/massing-pdf',
  },
  {
    name: 'PDF.js (pdfjs-dist)',
    use: 'Leitura e desenho das páginas de PDF na revisão de desenhos.',
    licence: 'Apache-2.0',
    href: 'https://github.com/mozilla/pdf.js',
  },
  {
    name: 'pdf-lib',
    use: 'Escrita de PDF na exportação de desenhos anotados.',
    licence: 'MIT',
    href: 'https://github.com/Hopding/pdf-lib',
  },
  {
    name: 'loaders.gl (@loaders.gl/las, @loaders.gl/core)',
    use: 'Leitura de nuvens de pontos em LAS e LAZ. Traz o descompressor ' +
      'laz-perf em WebAssembly (Apache-2.0, Hobu Inc.).',
    licence: 'MIT (vis.gl / OpenJS Foundation)',
    href: 'https://github.com/visgl/loaders.gl',
  },
  {
    name: 'web-e57 (Prolincur Technologies)',
    use: 'Leitura de nuvens de pontos em E57. É a biblioteca Rust e57 ' +
      '(cry-inc) compilada para WebAssembly, servida a partir da nossa ' +
      'origem e nunca de um CDN.',
    licence: 'Apache-2.0',
    href: 'https://github.com/prolincur/web-e57',
  },
  {
    name: 'ASTM E2807 (E57)',
    use: 'A norma aberta do formato E57, que define o cabeçalho, o XML ' +
      'com o protótipo dos campos e a secção binária que o leitor ' +
      'descodifica.',
    licence: 'Norma ASTM International',
    href: 'https://www.astm.org/e2807-11r19e01.html',
  },
  {
    name: 'ifc-georeferencer (bedrock-engineer / buildingSMART NL)',
    use: 'Referência das convenções de georreferenciação que gravamos no IFC: IfcProjectedCRS + IfcMapConversion no IFC4 e os property sets ePSet_MapConversion / ePSet_ProjectedCRS no IfcProject em IFC2X3, incluindo o tratamento de unidades. Implementação nossa, convenções deles.',
    licence: 'Apache-2.0',
    href: 'https://github.com/bedrock-engineer/ifc-georeferencer',
  },
  {
    name: 'liteAECO (Luis Torrecilla)',
    use: 'Motor de fusão de IFC da ferramenta Juntar IFC, incluído sem alterações e a correr no browser. Ideias do inventário de Psets, de acrescentar propriedades a partir de Excel, de mudar o nome e apagar Psets, e do IDS a partir de uma folha.',
    licence: 'Apache-2.0',
    href: 'https://github.com/ltorrecilla/liteaeco',
  },
  {
    name: 'IFClite (LTplus-AG)',
    use: 'Ideias: gráficos ligados ao modelo, selecção visível através do modelo, copiar Psets partilhados antes de os mudar, identidade estável, comparação por conteúdo, propriedades do IFC5, e a ordem das fontes de georreferenciação (IfcMapConversion, ePset_MapConversion do IFC2X3, IfcSite). Implementação nossa.',
    licence: 'MPL-2.0',
    href: 'https://github.com/LTplus-AG/ifc-lite',
  },
  {
    name: 'EPSG Geodetic Parameter Dataset (IOGP), via epsg.io',
    use: 'Sistemas de coordenadas: cerca de 6400 vêm incluídos (gerados da base de dados do PROJ, que é o EPSG Dataset); os que faltarem pedem-se ao epsg.io.',
    licence: 'Termos de utilização do EPSG Dataset',
    href: 'https://epsg.org/terms-of-use.html',
  },
  {
    name: 'buildingSMART International',
    use: 'Especificações abertas IFC, BCF e IDS, e os casos de teste oficiais IDS usados na validação de conformidade.',
    licence: 'CC BY-ND 4.0 (casos de teste IDS)',
    href: 'https://github.com/buildingSMART/IDS',
  },
  {
    name: 'Verificação da georreferência: LoGeoRef e "The Hidden Minefield of IFC Georeferencing"',
    use:
      'Os níveis LoGeoRef 10 a 50 são de C. Clemen e H. Görne, "Level of Georeferencing (LoGeoRef) using IFC for BIM", ' +
      'Journal of Geodesy, Cartography and Cadastre 10 (2019). As verificações (sistema, unidades, Scale, origem, ' +
      'norte verdadeiro com a convergência, os dois sítios, distância à origem) seguem o artigo de T. Krijnen e ' +
      'J. Gevaert na BIM Business (22/10/2026).',
    licence: 'Referência (artigos)',
    href: 'https://bimbusiness.substack.com/p/the-hidden-minefield-of-ifc-georeferencing',
  },
]

function Diagnostics() {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<Tab>('versao')
  useShortcutHandler('topbar.toggleDiagnostics', () => setOpen((v) => !v))
  const ref = useRef<HTMLDivElement | null>(null)
  const activeModelId = useAppStore((s) => s.activeModelId)
  const [status, setStatus] = useState<RawIfcStatus>('idle')

  useEffect(() => {
    const refresh = () => {
      if (!activeModelId) {
        setStatus('idle')
        return
      }
      setStatus(rawIfc.getStatus(activeModelId))
    }
    refresh()
    return rawIfc.subscribe(refresh)
  }, [activeModelId])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // The pill dot reflects the parser status, so the user notices
  // something needing attention without having to open the popover.
  // (Até à beta.200 ficava âmbar com um IFC 4.3 aberto; deixou de haver
  // essa limitação.)
  const dot = statusDotClass(status)

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center rounded border border-[var(--color-border)] bg-[var(--color-surface)]/60 px-1.5 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        title="Sobre / Diagnóstico: versão, funcionalidades, limitações, diagnóstico, ajuda"
        aria-label="Sobre e diagnóstico"
      >
        <Info size={15} />
        <span
          className={`absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ring-1 ring-[var(--color-panel)] ${dot}`}
        />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-[900] mt-1 flex max-h-[80vh] w-[520px] flex-col rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] text-xs shadow-lg">
          <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
            <span className="text-sm font-semibold text-[var(--color-text)]">
              VN2R openBIM Hub
            </span>
            <span className="rounded bg-[var(--color-warn-bg)] px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-[var(--color-warn-text)]">
              {CHANNEL}
            </span>
            <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
              v{VERSION}
            </span>
          </div>
          <div className="flex shrink-0 gap-0.5 border-b border-[var(--color-border)] bg-[var(--color-bg)]/40 px-2 py-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={
                  'rounded px-2 py-0.5 text-[11px] ' +
                  (tab === t.id
                    ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]')
                }
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            {tab === 'versao' && <VersionTab />}
            {tab === 'funcionalidades' && <FeaturesTab />}
            {tab === 'limitacoes' && <LimitationsTab />}
            {tab === 'diagnostico' && <DiagnosticsTab />}
            {tab === 'ajuda' && <HelpTab />}
            {tab === 'creditos' && <CreditsTab />}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- tabs

function VersionTab() {
  const current = RELEASES[0]
  return (
    <div>
      <BuildInfoSection />
      {current && <ReleaseEntry note={current} initialOpen highlight />}
      {RELEASES.length > 1 && (
        <div className="mt-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
            Versões anteriores
          </div>
          {RELEASES.slice(1).map((r) => (
            <ReleaseEntry key={r.version} note={r} />
          ))}
        </div>
      )}
    </div>
  )
}

function ReleaseEntry({
  note,
  initialOpen = false,
  highlight = false,
}: {
  note: ReleaseNote
  initialOpen?: boolean
  highlight?: boolean
}) {
  const [open, setOpen] = useState(initialOpen)
  const wrapper = highlight
    ? 'mb-2 rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)]/30 p-2'
    : 'mb-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)]/40 p-2'
  return (
    <div className={wrapper}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="font-mono text-[var(--color-text)]">v{note.version}</span>
        <span className="text-[10px] text-[var(--color-text-muted)]">
          {note.date}
        </span>
      </button>
      {open && (
        <div className="mt-1.5 text-[var(--color-text-muted)]">
          {note.summary && <div className="mb-1.5">{note.summary}</div>}
          {note.added && note.added.length > 0 && (
            <ChangeList title="Adicionado" items={note.added} />
          )}
          {note.changed && note.changed.length > 0 && (
            <ChangeList title="Alterado" items={note.changed} />
          )}
          {note.fixed && note.fixed.length > 0 && (
            <ChangeList title="Corrigido" items={note.fixed} />
          )}
          {note.removed && note.removed.length > 0 && (
            <ChangeList title="Removido" items={note.removed} />
          )}
        </div>
      )}
    </div>
  )
}

function ChangeList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="mb-1.5">
      <div className="mb-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
        {title}
      </div>
      <ul className="ml-3 list-disc text-[11px]">
        {items.map((it, i) => (
          <li key={i} className="mb-0.5">
            {it}
          </li>
        ))}
      </ul>
    </div>
  )
}

function FeaturesTab() {
  const { mode, modules } = useAuth()
  const enabled = useMemo(() => new Set(modules), [modules])
  const isCloud = mode === 'cloud'
  return (
    <div>
      <div className="mb-2 text-[var(--color-text-muted)]">
        O viewer base está sempre presente. Os módulos opcionais
        aparecem nos tabs da topbar quando estão activos no workspace.
      </div>
      {FEATURES.map((g) => (
        <FeatureGroupBlock
          key={g.title}
          group={g}
          enabledModules={enabled}
          isCloud={isCloud}
        />
      ))}
    </div>
  )
}

function FeatureGroupBlock({
  group,
  enabledModules,
  isCloud,
}: {
  group: FeatureGroup
  enabledModules: Set<string>
  isCloud: boolean
}) {
  const isModule = !!group.module
  const isEnabledHere = isModule
    ? isCloud && enabledModules.has(group.module!)
    : true
  return (
    <div className="mb-2.5">
      <div className="mb-0.5 flex items-center gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text)]">
          {group.title}
        </span>
        {isModule && (
          <span
            className={
              'rounded px-1.5 py-0.5 text-[9px] uppercase ' +
              (isEnabledHere
                ? 'bg-[var(--color-ok-bg)] text-[var(--color-ok-text)]'
                : 'bg-[var(--color-bg)]/60 text-[var(--color-text-dim)]')
            }
            title={
              isEnabledHere
                ? 'Activo neste workspace'
                : 'Não activo neste workspace: contacta a VN2R para activar'
            }
          >
            {isEnabledHere ? 'Activo' : 'Opcional'}
          </span>
        )}
      </div>
      <ul className="ml-3 list-disc text-[var(--color-text-muted)]">
        {group.items.map((it, i) => (
          <li key={i} className="mb-0.5 leading-snug">
            {it}
          </li>
        ))}
      </ul>
    </div>
  )
}

function LimitationsTab() {
  return (
    <div>
      <div className="mb-2 text-[var(--color-text-muted)]">
        Lista das limitações conhecidas desta versão. Workarounds
        descritos onde aplicável.
      </div>
      {LIMITATIONS.map((l, i) => (
        <LimitationBlock key={i} limitation={l} />
      ))}
    </div>
  )
}

function LimitationBlock({ limitation }: { limitation: KnownLimitation }) {
  const colour =
    limitation.severity === 'warn'
      ? 'border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] text-[var(--color-warn-text)]'
      : 'border-[var(--color-border)] bg-[var(--color-bg)]/40 text-[var(--color-text-muted)]'
  return (
    <div className={`mb-2 rounded border p-2 ${colour}`}>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide">
        {limitation.title}
      </div>
      <div className="leading-snug">{limitation.detail}</div>
      {limitation.workaround && (
        <div className="mt-1.5 leading-snug">
          <span className="font-semibold">Workaround: </span>
          {limitation.workaround}
        </div>
      )}
    </div>
  )
}

function DiagnosticsTab() {
  const activeModelId = useAppStore((s) => s.activeModelId)
  const classColorsEnabled = useAppStore((s) => s.classColorsEnabled)
  const setClassColorsEnabled = useAppStore((s) => s.setClassColorsEnabled)
  const edgeThreshold = useAppStore((s) => s.edgeThreshold)
  const [status, setStatus] = useState<RawIfcStatus>('idle')
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    const refresh = () => {
      if (!activeModelId) {
        setStatus('idle')
        setError(undefined)
        return
      }
      setStatus(rawIfc.getStatus(activeModelId))
      setError(rawIfc.getError(activeModelId))
    }
    refresh()
    return rawIfc.subscribe(refresh)
  }, [activeModelId])

  const onToggleClassColors = (next: boolean) => {
    setClassColorsEnabled(next)
    void viewerApi.repaintAllModels({ reset: true })
  }

  return (
    <div>
      <div className="mb-3">
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
          Parser IFC paralelo (web-ifc)
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[var(--color-text-muted)]">
            Estado neste modelo
          </span>
          <RawStatusBadge status={status} />
        </div>
        <div className="mt-1 text-[var(--color-text-muted)]">
          Configura ligado/desligado <strong>antes</strong> de abrir cada
          IFC, no toggle <em>Parser IFC paralelo</em> ao lado do botão
          "Abrir modelo".
        </div>
        {status === 'ready' && (
          <div className="mt-1 text-[var(--color-ok-text)]">
            Já está carregado neste modelo, não vai recarregar.
          </div>
        )}
        {error && (
          <div className="mt-1 rounded border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-2 py-1 text-[var(--color-error-text)]">
            {error}
          </div>
        )}
      </div>

      <div className="mb-3">
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
          Cores por classe IFC
        </div>
        <label className="flex cursor-pointer items-center justify-between gap-2">
          <span className="text-[var(--color-text-muted)]">
            Pintar elementos por classe (HVAC laranja, ducts amarelo,
            paredes cinzentas, …)
          </span>
          <input
            type="checkbox"
            checked={classColorsEnabled}
            onChange={(e) => onToggleClassColors(e.target.checked)}
            className="h-3.5 w-3.5"
          />
        </label>
        <div className="mt-1 text-[10px] text-[var(--color-text-muted)]">
          IFCs sem <code>IfcSurfaceStyle</code> aparecem brancos; esta
          palette por defeito alinha-se com BIMvision / Solibri / Bonsai.
          Mantém os overlays de selecção e ghost.
        </div>
      </div>

      <div className="mb-3">
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
          Arestas / linhas BIM
        </div>
        <label className="flex flex-col gap-1">
          <span className="flex items-center justify-between text-[var(--color-text-muted)]">
            <span>
              Threshold de ângulo entre faces para desenhar aresta
            </span>
            <span className="font-mono text-[var(--color-text)]">
              {edgeThreshold}°
            </span>
          </span>
          <input
            type="range"
            min={5}
            max={80}
            step={1}
            value={edgeThreshold}
            onChange={(e) =>
              viewerApi.setEdgeThreshold(Number(e.target.value))
            }
            className="w-full"
          />
          <div className="flex justify-between text-[9px] text-[var(--color-text-dim)]">
            <span>5° (mais arestas)</span>
            <span>30° (default)</span>
            <span>80° (só ângulos vivos)</span>
          </div>
        </label>
        <div className="mt-1 text-[10px] text-[var(--color-text-muted)]">
          Tubagens / fittings curvos são tesselados em segmentos
          pequenos com ângulos abaixo de 30° entre faces, por isso
          parecem lisos. Baixar o threshold mostra esses seams; subir
          esconde detalhes legítimos. Cada alteração regenera as
          arestas de todos os modelos.
        </div>
      </div>

      <IfcCacheSection />
    </div>
  )
}

function HelpTab() {
  return (
    <div>
      {HELP.map((h, i) => (
        <div
          key={i}
          className="mb-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)]/40 p-2"
        >
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text)]">
            {h.title}
          </div>
          <div className="whitespace-pre-line text-[var(--color-text-muted)]">
            {h.body}
          </div>
        </div>
      ))}
    </div>
  )
}

function CreditsTab() {
  return (
    <div>
      <div className="mb-2 text-[var(--color-text-muted)]">
        Esta aplicação assenta em trabalho aberto de terceiros. Ficam
        aqui os créditos, com a licença de cada projeto e a ligação para
        a fonte. Obrigado a quem o mantém.
      </div>
      <ul className="flex flex-col gap-1.5">
        {CREDITS.map((c) => (
          <li
            key={c.name}
            className="rounded border border-[var(--color-border)] bg-[var(--color-bg)]/40 p-2"
          >
            <div className="mb-0.5 flex items-start justify-between gap-2">
              <span className="text-[11px] font-semibold text-[var(--color-text)]">
                {c.name}
              </span>
              <span className="shrink-0 rounded bg-[var(--color-bg)]/60 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-[var(--color-text-dim)]">
                {c.licence}
              </span>
            </div>
            <div className="leading-snug text-[var(--color-text-muted)]">
              {c.use}
            </div>
            <a
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-0.5 inline-block text-[10px] text-[var(--color-accent)] hover:underline"
            >
              {c.href}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------- helpers

/** Build provenance: commit + ISO build time, both injected at build
 * time by `vite.config.ts`. Useful when the user reports a UI bug:
 * the response can be confidently checked against the deployed
 * commit instead of guessing whether the browser has stale code. */
function BuildInfoSection() {
  const commit = __BUILD_COMMIT__
  const builtAt = __BUILD_TIME__
  const githubUrl =
    commit !== 'unknown'
      ? `https://github.com/RPGAVINA/vn2r-openbim-hub/commit/${commit}`
      : null
  const builtLabel = (() => {
    try {
      return new Date(builtAt).toLocaleString('pt-PT', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return builtAt
    }
  })()
  return (
    <div className="mb-3 rounded border border-[var(--color-border)] bg-[var(--color-bg)]/50 p-2 text-[10px] text-[var(--color-text-muted)]">
      <div className="mb-1 font-semibold uppercase tracking-wide text-[var(--color-text-dim)]">
        Build
      </div>
      <div className="flex items-center justify-between">
        <span>Commit</span>
        {githubUrl ? (
          <a
            href={githubUrl}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-[var(--color-accent)] hover:underline"
          >
            {commit}
          </a>
        ) : (
          <span className="font-mono">{commit}</span>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span>Compilado</span>
        <span className="font-mono">{builtLabel}</span>
      </div>
      <div className="mt-1 text-[var(--color-text-dim)]">
        Se o teu browser não está a mostrar o que esperas, compara o
        commit acima com o último em GitHub.
      </div>
    </div>
  )
}

function IfcCacheSection() {
  const [records, setRecords] = useState<IfcBlobRecord[]>([])
  const [usage, setUsage] = useState<number | null>(null)
  const [quota, setQuota] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  const refresh = async () => {
    const [rs, est] = await Promise.all([
      ifcPersistence.list(),
      estimateOriginStorage(),
    ])
    setRecords(rs.sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt)))
    setUsage(est.usageBytes)
    setQuota(est.quotaBytes)
  }
  useEffect(() => {
    // A regra não vê para lá do `await`: o `refresh` só escreve
    // estado depois do `await Promise.all(...)`, por isso não há
    // render em cascata para evitar aqui.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh()
  }, [])

  const totalIfcBytes = records.reduce((n, r) => n + r.sizeBytes, 0)
  const pct =
    usage !== null && quota !== null && quota > 0
      ? Math.min(100, (usage / quota) * 100)
      : null
  const warn = pct !== null && pct > 80

  const onClearAll = async () => {
    if (!confirm(`Apagar ${records.length} IFC(s) em cache local?`)) return
    setBusy(true)
    try {
      await ifcPersistence.clearAll()
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  const onRemove = async (sha256: string) => {
    setBusy(true)
    try {
      await ifcPersistence.remove(sha256)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
        <span>Cache de IFCs</span>
        {warn && (
          <span className="rounded bg-[var(--color-warn-bg)] px-1.5 py-0.5 text-[9px] uppercase text-[var(--color-warn-text)]">
            Storage {pct?.toFixed(0)}%
          </span>
        )}
      </div>
      <div className="mb-1 text-[var(--color-text-muted)]">
        Os bytes dos IFC carregados ficam guardados localmente para que{' '}
        <em>Abrir sessão</em> os reabra automaticamente. Os bytes nunca
        saem do teu computador.
      </div>
      {pct !== null && (
        <div className="mb-1.5">
          <div className="h-1 w-full overflow-hidden rounded bg-[var(--color-bg)]">
            <div
              className="h-full transition-[width]"
              style={{
                width: `${pct.toFixed(2)}%`,
                background: warn ? '#f59e0b' : 'var(--color-accent)',
              }}
            />
          </div>
          <div className="mt-0.5 flex items-center justify-between text-[9px] text-[var(--color-text-dim)]">
            <span>
              Origem: {formatBytes(usage)} / {formatBytes(quota)} (
              {pct.toFixed(1)}%)
            </span>
            <span>
              IFCs: {formatBytes(totalIfcBytes)} ({records.length})
            </span>
          </div>
        </div>
      )}
      {records.length === 0 ? (
        <div className="text-[var(--color-text-dim)]">Sem IFCs em cache.</div>
      ) : (
        <ul className="flex flex-col gap-1 text-[10px]">
          {records.map((r) => (
            <li
              key={r.sha256}
              className="flex items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-panel)] px-2 py-1"
            >
              <span className="flex-1 truncate" title={r.sha256}>
                {r.filename}
              </span>
              <span className="text-[9px] text-[var(--color-text-dim)]">
                {formatBytes(r.sizeBytes)}
              </span>
              <button
                type="button"
                onClick={() => void onRemove(r.sha256)}
                disabled={busy}
                title="Apagar este ficheiro da cache (Abrir sessão vai pedir o ficheiro do disco)"
                className="rounded px-1.5 py-0.5 text-[9px] text-[var(--color-text-muted)] hover:bg-[var(--color-panel-elevated)] hover:text-red-400"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {records.length > 0 && (
        <div className="mt-1 flex justify-end">
          <button
            type="button"
            onClick={() => void onClearAll()}
            disabled={busy}
            className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            Limpar tudo
          </button>
        </div>
      )}
    </div>
  )
}

function statusDotClass(s: RawIfcStatus): string {
  switch (s) {
    case 'ready':
      return 'bg-emerald-400'
    case 'loading':
      return 'bg-[var(--color-accent)] animate-pulse'
    case 'failed':
      return 'bg-red-400'
    default:
      return 'bg-[var(--color-text-dim)]'
  }
}

function RawStatusBadge({ status }: { status: RawIfcStatus }) {
  const map: Record<RawIfcStatus, { label: string; cls: string }> = {
    idle: {
      label: 'Não carregado',
      cls: 'bg-[var(--color-bg)]/60 text-[var(--color-text-muted)]',
    },
    loading: {
      label: 'A carregar…',
      cls: 'bg-[var(--color-warn-bg)] text-[var(--color-warn-text)]',
    },
    ready: {
      label: 'Carregado',
      cls: 'bg-[var(--color-ok-bg)] text-[var(--color-ok-text)]',
    },
    failed: {
      label: 'Falhou',
      cls: 'bg-[var(--color-error-bg)] text-[var(--color-error-text)]',
    },
  }
  const info = map[status]
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${info.cls}`}>
      {info.label}
    </span>
  )
}

export default Diagnostics
